(() => {
  const escape = (value) =>
    String(value ?? "").replace(
      /[&<>"']/g,
      (c) =>
        ({
          "&": "&amp;",
          "<": "&lt;",
          ">": "&gt;",
          '"': "&quot;",
          "'": "&#39;",
        })[c],
    );
  function init(el) {
    if (el.dataset.ready || !el.dataset.customer) return;
    el.dataset.ready = "1";
    const root = el.querySelector("[data-b2b-root]"),
      he = true;
    if (!root) return;
    const t = (_en, iw) => iw,
      money = (n) =>
        new Intl.NumberFormat(he ? "he-IL" : "en-IL", {
          style: "currency",
          currency: "ILS",
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(n / 100);
    const dialog = el.querySelector(".b2b-desk-dialog");
    const launch = el.querySelector("[data-start-order]");
    let previousOverflow = "",
      previousGutter = "",
      previousBodyOverflow = "",
      returnFocus = null;
    const openWorkspace = (opener) => {
      if (!dialog) return;
      if (dialog.open) return;
      returnFocus = opener || document.activeElement;
      previousOverflow = document.documentElement.style.overflow;
      previousGutter = document.documentElement.style.scrollbarGutter;
      previousBodyOverflow = document.body.style.overflow;
      document.documentElement.style.scrollbarGutter = "stable";
      document.documentElement.style.overflow = "hidden";
      document.body.style.overflow = "hidden";
      dialog.showModal();
      fitHeight();
      fitMobileViewport();
    };
    if (dialog) {
      launch.onclick = () => openWorkspace(launch);
      el.querySelector("[data-close-order]").onclick = () => dialog.close();
      dialog.addEventListener("close", () => {
        document.documentElement.style.overflow = previousOverflow;
        document.documentElement.style.scrollbarGutter = previousGutter;
        document.body.style.overflow = previousBodyOverflow;
        if (location.hash === "#b2b-order") {
          const url = new URL(location.href);
          url.hash = "";
          history.replaceState(history.state, "", url);
        }
        const target =
          returnFocus?.isConnected && returnFocus !== document.body
            ? returnFocus
            : launch;
        if (!target.closest("[hidden]")) target.focus({ preventScroll: true });
      });
    }
    let endpoint =
      el.dataset.endpoint +
      "?location=" +
      encodeURIComponent(el.dataset.location);
    let key = "ops-b2b-v1:" + el.dataset.customer + ":" + el.dataset.location;
    let data,
      selected = "",
      quantities = {},
      quote = null,
      busy = false,
      error = "",
      notes = "",
      stage = "build",
      search = "",
      saveError = false,
      mobileView = "products",
      mobileCatalogOpen = false;
    const mobileQuery =
      "(max-width: 767px), (max-width: 1100px) and (max-height: 500px) and (pointer: coarse)";
    const isMobile = () =>
      window.matchMedia?.(mobileQuery).matches ?? window.innerWidth <= 767;
    const collapsedGroups = new Set();
    let idempotency = crypto.randomUUID().replaceAll("-", "");
    const restore = () => {
      quantities = {};
      notes = "";
      idempotency = crypto.randomUUID().replaceAll("-", "");
      try {
        const saved = JSON.parse(localStorage.getItem(key) || "null");
        if (saved && typeof saved.quantities === "object") {
          quantities = saved.quantities;
          notes = saved.notes || "";
          idempotency = saved.idempotency || idempotency;
        }
      } catch {}
    };
    restore();
    const save = () => {
      try {
        localStorage.setItem(
          key,
          JSON.stringify({ quantities, notes, idempotency }),
        );
        saveError = false;
      } catch {
        saveError = true;
      }
    };
    const lines = () =>
      Object.entries(quantities)
        .filter(([, q]) => Number(q) > 0)
        .map(([variantId, quantity]) => ({
          variantId,
          quantity: Number(quantity),
        }));
    const selectedLines = (includeInvalid = false) =>
      data.products.flatMap((p) =>
        p.variants
          .filter((v) =>
            includeInvalid
              ? Number(quantities[v.id] || 0) !== 0
              : quantities[v.id] > 0,
          )
          .map((v) => ({ p, v, q: Number(quantities[v.id]) })),
      );
    const price = (v, q) =>
      [...v.breaks]
        .sort((a, b) => b.minimum - a.minimum)
        .find((b) => q >= b.minimum)?.price ?? v.price;
    function message(error) {
      const text = error?.message || String(error || "");
      if (/[א-ת]/.test(text)) return text;
      const stock = text.match(/^(.*): only (\d+) units/);
      if (stock)
        return `${stock[1]}: נותרו ${stock[2]} יחידות להזמנה. יש לעדכן את הכמות.`;
      const quantity = text.match(
        /^(.*): use at least (\d+), in multiples of (\d+)/,
      );
      if (quantity)
        return `${quantity[1]}: מינימום ${quantity[2]} יחידות, בכפולות של ${quantity[3]}.`;
      if (/session|sign in|account|access/i.test(text))
        return "יש לרענן את העמוד ולהתחבר לחשבון החברה.";
      if (/catalog|no longer/i.test(text))
        return "הקטלוג השתנה. יש לרענן את העמוד ולבדוק את הבחירה.";
      if (/shipping address/i.test(text))
        return "חסרה כתובת משלוח. פנו אלינו לעדכון הכתובת.";
      if (/paused|not enabled|unavailable/i.test(text))
        return "ההזמנות אינן זמינות כרגע. נסו שוב מאוחר יותר.";
      return "לא ניתן להשלים את הבקשה. נסו לרענן את העמוד.";
    }
    async function api(body) {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...body, actionToken: data.actionToken }),
      });
      const result = await response.json();
      if (!response.ok || result.error)
        throw new Error(
          result.error ||
            t("Unable to complete the request.", "לא ניתן להשלים את הבקשה."),
        );
      return result;
    }
    function estimate() {
      const chosen = selectedLines();
      const raw = chosen.reduce((n, l) => n + price(l.v, l.q) * l.q, 0);
      const rate = (data.estimatedVatPercent || 0) / 100;
      const merchandise = data.taxesIncluded
        ? raw
        : Math.round(raw * (1 + rate));
      const footwear = chosen
        .filter((l) => !l.p.accessory)
        .reduce((n, l) => n + l.q, 0);
      const shipping =
        !chosen.length ||
        (data.freeShippingMinimum !== null &&
          merchandise >= data.freeShippingMinimum)
          ? 0
          : footwear
            ? Math.ceil(footwear / data.shippingBandUnits) *
              data.shippingPerBand
            : data.accessoryOnlyShipping;
      const total = merchandise + shipping;
      const tax = Math.round(total - total / (1 + rate));
      const invalid =
        Object.values(quantities).some(
          (q) => !Number.isFinite(Number(q)) || Number(q) < 0,
        ) ||
        chosen.some(
          (l) =>
            !Number.isInteger(l.q) ||
            l.q < l.v.minimum ||
            l.q > l.v.max ||
            l.q % l.v.increment !== 0,
        );
      return {
        chosen,
        merchandise,
        shipping,
        total,
        tax,
        count: chosen.reduce((n, l) => n + l.q, 0),
        invalid,
      };
    }
    function progress(value) {
      const minimum = data.minimum;
      const free = data.freeShippingMinimum;
      let target, label;
      if (value < minimum) {
        target = minimum;
        label = `עוד ${money(Math.ceil((minimum - value) / 100) * 100)} למינימום ההזמנה`;
      } else if (free !== null && value < free) {
        target = free;
        label = `עוד ${money(Math.ceil((free - value) / 100) * 100)} למשלוח חינם`;
      } else {
        target = free || minimum;
        label = free !== null ? "המשלוח ללא עלות" : "הגעת למינימום ההזמנה";
      }
      if (!target) return "";
      return `<div class="b2b-desk-progress"><div><span>${label}</span></div><progress max="${target}" value="${Math.min(value, target)}" aria-label="${label}"></progress></div>`;
    }
    function totals(total, tax, shipping, estimated = false) {
      return `<dl><div><dt>מוצרים כולל מע״מ</dt><dd>${money(total - shipping)}</dd></div><div><dt>משלוח${estimated ? " משוער" : ""}</dt><dd>${shipping ? money(shipping) : "ללא עלות"}</dd></div><div><dt>סה״כ לפני מע״מ</dt><dd>${money(total - tax)}</dd></div><div><dt>מע״מ</dt><dd>${money(tax)}</dd></div><div class="b2b-desk-total"><dt>סה״כ${estimated ? " משוער" : ""}</dt><dd>${money(total)}</dd></div></dl>`;
    }
    function quantityInvalid(v, q) {
      return (
        q !== 0 &&
        (!Number.isInteger(q) ||
          q < v.minimum ||
          q > v.max ||
          q % v.increment !== 0)
      );
    }
    function summary(mobile = false) {
      const e = estimate();
      const below = e.merchandise < data.minimum;
      const disabled = !e.chosen.length || below || e.invalid || busy;
      const info = below
        ? `מינימום הזמנה ${money(data.minimum)}`
        : e.invalid
          ? "יש לתקן את הכמויות המסומנות"
          : "";
      if (mobile)
        return `<div class="b2b-desk-mobile-summary"><span><strong>${money(e.total)}</strong><small>${info || e.count + " יחידות · כולל משלוח ומע״מ"}</small></span><button type="button" class="b2b-desk-button" data-action="review" ${disabled ? "disabled" : ""}>${busy ? "מחשבים…" : "המשך לשלב הבא"}</button></div>`;
      const groups = new Map();
      selectedLines(true).forEach((line) => {
        if (!groups.has(line.p.id))
          groups.set(line.p.id, { product: line.p, lines: [] });
        groups.get(line.p.id).lines.push(line);
      });
      const rows = [...groups.values()]
        .map(
          ({ product, lines }) =>
            `<section class="b2b-desk-summary-group"><h3>${escape(product.title)}</h3>${lines.map((l) => `<div class="b2b-desk-summary-row" data-summary-row="${escape(l.v.id)}"><span class="b2b-desk-line-name">${escape(l.v.title === "Default Title" ? "יחידות" : l.v.title)}</span><input class="b2b-desk-summary-quantity" type="number" inputmode="numeric" min="0" max="${l.v.max}" step="${l.v.increment}" data-summary-variant="${escape(l.v.id)}" value="${l.q}" aria-invalid="${quantityInvalid(l.v, l.q)}" aria-describedby="summary-${helpId(l.v)}" aria-label="כמות ${escape(product.title + " " + l.v.title)}"><span data-line-amount>${money(price(l.v, l.q) * Math.max(0, l.q))}</span><small class="b2b-desk-line-error" id="summary-${helpId(l.v)}" ${quantityInvalid(l.v, l.q) ? "" : "hidden"}>${quantityHelp(l.v, l.q)}</small></div>`).join("")}</section>`,
        )
        .join("");
      return `<aside class="b2b-desk-summary"><div class="b2b-desk-summary-head"><h2>ההזמנה שלך</h2><span>${e.count} יחידות</span></div><div class="b2b-desk-summary-lines">${rows ? `<div class="b2b-desk-summary-columns" aria-hidden="true"><span>פריט</span><span>כמות</span><span>סכום</span></div>${rows}` : '<p class="b2b-desk-empty">עדיין לא נבחרו מוצרים</p>'}</div><div class="b2b-desk-summary-footer">${progress(e.merchandise)}${totals(e.total, e.tax, e.shipping, true)}<button type="button" class="b2b-desk-button" data-action="review" ${disabled ? "disabled" : ""}>${busy ? "מחשבים…" : "המשך לשלב הבא"}</button>${info ? `<p class="b2b-desk-info">${info}</p>` : ""}${saveError ? '<p class="b2b-desk-info">לא ניתן לשמור את הבחירה בדפדפן הזה</p>' : ""}</div></aside>`;
    }

    const quantityHelp = (v, q) =>
      q > v.max
        ? v.max === 0
          ? "אזל המלאי"
          : `עד ${v.max} יחידות בהזמנה`
        : q < 0
          ? "יש להזין כמות חיובית"
          : q > 0 && q < v.minimum
            ? `מינימום ${v.minimum}`
            : `כפולות של ${v.increment}`;
    const productSubtotal = (p) =>
      p.variants.reduce(
        (n, v) =>
          n +
          price(v, Number(quantities[v.id]) || 0) *
            (Number(quantities[v.id]) || 0),
        0,
      );
    function productTotal(p) {
      const count = p.variants.reduce(
        (n, v) => n + (Number(quantities[v.id]) || 0),
        0,
      );
      return count > 0
        ? `<span>${count} יחידות</span><strong>${money(productSubtotal(p))}</strong>`
        : "";
    }
    const helpId = (v) => "b2b-qty-" + el.id + "-" + v.id.split("/").pop();
    function matrix(p) {
      const sizeOf = (v) =>
        v.options.find((o) => /size|מידה|מידות/i.test(o.name))?.value || "כמות";
      const colorOf = (v) =>
        v.options
          .filter((o) => !/size|מידה|מידות/i.test(o.name))
          .map((o) => o.value)
          .filter((v) => v !== "Default Title")
          .join(" / ") || "יחידות";
      const sizes = [...new Set(p.variants.map(sizeOf))].sort((a, b) =>
        a.localeCompare(b, "he", { numeric: true }),
      );
      const colors = [...new Set(p.variants.map(colorOf))];
      const prices = p.variants.map((v) =>
        price(v, Number(quantities[v.id]) || 1),
      );
      const low = prices.length ? Math.min(...prices) : 0,
        high = prices.length ? Math.max(...prices) : 0;
      const swatch = (c) => {
        const sw = (p.swatches || []).find((v) => v.name === c);
        if (sw?.image && /^https:\/\//.test(sw.image))
          return `<img class="b2b-desk-swatch" src="${escape(sw.image)}" alt="">`;
        if (sw?.color && /^#[0-9a-f]{3,8}$/i.test(sw.color))
          return `<span class="b2b-desk-swatch" style="background-color:${escape(sw.color)}" aria-hidden="true"></span>`;
        return "";
      };
      return `<section class="b2b-desk-product"><header>${p.image ? `<img src="${escape(p.image)}" alt="" width="64" height="64">` : ""}<div><h2>${escape(p.title)}</h2><div class="b2b-desk-product-price"><strong>${money(low)}${high !== low ? "–" + money(high) : ""}</strong><small>ליחידה${data.taxesIncluded ? " כולל מע״מ" : " לפני מע״מ"}</small></div></div><div class="b2b-desk-product-subtotal" data-product-subtotal aria-live="polite">${productTotal(p)}</div></header><div class="b2b-desk-scroll-hint" data-scroll-hint hidden><svg width="18" height="14" viewBox="0 0 24 18" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M3 9h18M7 5 3 9l4 4m10-8 4 4-4 4"/></svg>גללו לצדדים למידות נוספות</div><div class="b2b-desk-table-scroll" data-matrix-scroll tabindex="0" aria-label="מידות וכמויות"><table style="--matrix-min:${Math.min(112 + sizes.length * 44, 900)}px"><thead><tr><th>${sizes[0] === "כמות" ? "מוצר" : "צבע / מידה"}</th>${sizes.map((v) => `<th>${escape(v)}</th>`).join("")}</tr></thead><tbody>${colors
        .map(
          (c) =>
            `<tr><th><span class="b2b-desk-color">${swatch(c)}${escape(c)}</span></th>${sizes
              .map((size) => {
                const v = p.variants.find(
                  (v) => sizeOf(v) === size && colorOf(v) === c,
                );
                if (!v)
                  return '<td class="b2b-desk-unavailable" title="לא זמין" aria-label="לא זמין"></td>';
                const q = Number(quantities[v.id]) || 0;
                const invalid =
                  q !== 0 &&
                  (q > v.max || q < v.minimum || q % v.increment !== 0);
                return `<td class="${v.max === 0 ? "b2b-desk-unavailable" : ""}" ${v.max === 0 ? 'title="אזל המלאי" aria-label="אזל המלאי"' : ""}><input type="number" inputmode="numeric" min="0" max="${v.max}" step="${v.increment}" data-variant="${escape(v.id)}" value="${q || ""}" placeholder=" " ${v.max === 0 ? "disabled" : ""} aria-invalid="${invalid}" aria-describedby="${helpId(v)}" aria-label="${escape(p.title + " " + c + " " + size + (v.max === 0 ? " לא זמין" : " עד " + v.max))}" title="${escape(v.sku)} · ${v.max === 0 ? "אזל המלאי" : quantityHelp(v, q)}"><small id="${helpId(v)}" class="b2b-desk-quantity-help" role="status" ${invalid ? "" : "hidden"}>${quantityHelp(v, q)}</small></td>`;
              })
              .join("")}</tr>`,
        )
        .join(
          "",
        )}</tbody></table></div><p class="b2b-desk-grid-message" data-grid-message role="status"></p></section>`;
    }
    function productNavigation(visible) {
      const buttons = (visible) =>
        visible
          .map(
            (p) =>
              `<button type="button" data-product="${escape(p.id)}" aria-pressed="${selected === p.id}">${p.image ? `<img src="${escape(p.image)}" alt="" width="44" height="44" loading="lazy">` : ""}<span>${escape(p.title)}</span>${p.variants.reduce((n, v) => n + (Number(quantities[v.id]) || 0), 0) > 0 ? `<strong>${p.variants.reduce((n, v) => n + (Number(quantities[v.id]) || 0), 0)}</strong>` : ""}</button>`,
          )
          .join("");
      if (el.dataset.groupProducts === "false") return buttons(visible);
      const groups = new Map();
      visible.forEach((p) => {
        const id = p.category?.id || "other";
        if (!groups.has(id))
          groups.set(id, {
            name: p.category?.name || "מוצרים נוספים",
            products: [],
          });
        groups.get(id).products.push(p);
      });
      return [...groups]
        .map(
          ([id, g]) =>
            `<details data-group="${escape(id)}" ${search || !collapsedGroups.has(id) ? "open" : ""}><summary><span>${escape(g.name)}</span><small>${g.products.length}</small></summary><div class="b2b-desk-category-products">${buttons(g.products)}</div></details>`,
        )
        .join("");
    }
    function fitHeight() {
      const matrixScroll = root.querySelector("[data-matrix-scroll]");
      const hint = root.querySelector("[data-scroll-hint]");
      if (hint && matrixScroll)
        hint.hidden = matrixScroll.scrollWidth <= matrixScroll.clientWidth + 1;
      if (
        (el.dataset.fitViewport !== "true" && !dialog) ||
        (dialog && !dialog.open) ||
        window.innerWidth <= 1100
      )
        return;
      const layout = root.querySelector(".b2b-desk-layout");
      if (layout)
        el.style.setProperty(
          "--desk-height",
          Math.max(
            420,
            window.innerHeight -
              Math.max(16, layout.getBoundingClientRect().top) -
              24,
          ) + "px",
        );
    }
    const locationSwitch = el.querySelector("[data-location-switch]");
    if (locationSwitch)
      locationSwitch.onchange = async () => {
        if (busy) return;
        save();
        const nextLocation = locationSwitch.value;
        const nextEndpoint =
          el.dataset.endpoint + "?location=" + encodeURIComponent(nextLocation);
        busy = true;
        root.inert = true;
        locationSwitch.disabled = true;
        try {
          const response = await fetch(nextEndpoint);
          const result = await response.json();
          if (!response.ok || result.error)
            throw new Error(result.error || "לא ניתן להחליף סניף כרגע.");
          data = result;
          endpoint = nextEndpoint;
          el.dataset.location = nextLocation;
          key = "ops-b2b-v1:" + el.dataset.customer + ":" + nextLocation;
          restore();
          const allowed = new Set(
            data.products.flatMap((p) => p.variants.map((v) => v.id)),
          );
          quantities = Object.fromEntries(
            Object.entries(quantities).filter(([id]) => allowed.has(id)),
          );
          quote = null;
          stage = "build";
          search = "";
          error = "";
          collapsedGroups.clear();
          selected = data.products[0]?.id || "";
        } catch (e) {
          error = message(e);
        } finally {
          busy = false;
          root.inert = false;
          render();
        }
      };
    el.querySelector('[data-order-step="build"]')?.addEventListener(
      "click",
      () => {
        if (busy || stage !== "review") return;
        stage = "build";
        error = "";
        render();
      },
    );
    function mobileControls() {
      const count = estimate().count;
      const product = data.products.find((p) => p.id === selected);
      return `<div class="b2b-desk-mobile-controls"><div class="b2b-desk-mobile-tabs" role="group" aria-label="תצוגת ההזמנה"><button type="button" data-mobile-view="products" aria-pressed="${mobileView === "products"}">מוצרים</button><button type="button" data-mobile-view="order" aria-pressed="${mobileView === "order"}">ההזמנה שלך <span data-mobile-count>${count}</span></button></div><button type="button" class="b2b-desk-mobile-picker" data-mobile-catalog aria-expanded="${mobileCatalogOpen}" aria-controls="catalog-${escape(el.id)}"><span>בחירת מוצר</span><strong>${escape(product?.title || "כל המוצרים")}</strong><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg></button></div>`;
    }
    function reviewSubmit() {
      return `<button class="b2b-desk-button" data-action="submit" ${busy || !quote.hash || quote.merchandise < quote.minimum ? "disabled" : ""}>${busy ? (quote.hash ? "שולחים…" : "בודקים מחירים…") : "שליחת הזמנה"}</button>`;
    }
    function render() {
      if (!data) return;
      if (locationSwitch) {
        locationSwitch.hidden = (data.locations?.length || 0) < 2;
        locationSwitch.disabled = busy;
        locationSwitch.innerHTML = (data.locations || [])
          .map(
            (l) =>
              `<option value="${escape(l.locationId)}" ${l.locationId === data.buyer.locationId ? "selected" : ""}>${escape(l.companyName)} — ${escape(l.locationName)}</option>`,
          )
          .join("");
      }

      const locationLabel = el.querySelector("[data-location-label]");
      if (locationLabel) {
        locationLabel.hidden = (data.locations?.length || 0) > 1;
        locationLabel.textContent = `${data.buyer.companyName} — ${data.buyer.locationName}`;
      }
      const steps = el.querySelector("[data-order-steps]");
      if (steps) {
        steps.hidden = stage === "done";
        steps.querySelectorAll("[data-order-step]").forEach((step) => {
          if (step.dataset.orderStep === stage)
            step.setAttribute("aria-current", "step");
          else step.removeAttribute("aria-current");
          if (step.tagName === "BUTTON") {
            step.disabled = busy || stage !== "review";
            step.setAttribute(
              "aria-label",
              stage === "review"
                ? "חזרה לבחירת מוצרים וכמויות"
                : "בחירת מוצרים",
            );
          }
        });
      }
      root.dataset.stage = stage;
      root.dataset.mobileView = mobileView;
      root.dataset.catalogOpen = String(mobileCatalogOpen);
      el.querySelectorAll("[data-workspace-title]").forEach((title) => {
        title.textContent = "הזמנה חדשה";
      });
      if (stage === "done") {
        root.innerHTML = `<div class="b2b-desk-confirmation"><div class="b2b-desk-check" aria-hidden="true"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m5 12 4 4 10-10"/></svg></div><h2>${t("Your request is with us", "בקשת ההזמנה התקבלה")}</h2><p>${t("We will review quantities, shipping and payment terms before creating your order. Stock is not reserved until approval.", "ההזמנה הועברה לבדיקה. נעדכן לאחר האישור.")}</p><button class="b2b-desk-button" data-action="new">${t("Start another order", "להזמנה נוספת")}</button></div>`;
        bind();
        return;
      }
      const errorHtml = error
        ? `<p class="b2b-desk-error" role="alert">${escape(error)}</p>`
        : "";
      if (stage === "review" && quote) {
        root.innerHTML = `${errorHtml}<div class="b2b-desk-review"><section class="b2b-desk-review-main"><header><h2>סיכום ההזמנה</h2><span>${quote.lines.length} פריטים · ${quote.lines.reduce((sum, line) => sum + line.quantity, 0)} יחידות</span></header><div class="b2b-desk-table-scroll"><table><colgroup><col class="b2b-review-col-product"><col class="b2b-review-col-variant"><col class="b2b-review-col-sku"><col class="b2b-review-col-qty"></colgroup><thead><tr><th>מוצר</th><th>צבע / מידה</th><th>מק״ט</th><th>כמות</th></tr></thead><tbody>${quote.lines.map((l) => `<tr><td>${escape(l.title)}</td><td>${escape(l.variantTitle === "Default Title" ? "—" : l.variantTitle)}</td><td><bdi>${escape(l.sku)}</bdi></td><td>${l.quantity}</td></tr>`).join("")}</tbody></table></div></section><aside class="b2b-desk-review-summary"><label>הערות להזמנה<textarea data-field="notes" maxlength="2000" rows="3" placeholder="משהו שחשוב שנדע?">${escape(notes)}</textarea></label><div class="b2b-desk-review-footer">${totals(quote.total, quote.tax, quote.total - quote.merchandise, !quote.hash)}${!quote.hash ? '<p class="b2b-desk-muted" role="status">מעדכנים מחירים ומשלוח…</p>' : ""}<p class="b2b-desk-muted">לאחר השליחה ייתכנו שינויים בכמויות בהתאם לחוסרי מלאי</p><p class="b2b-desk-muted">הבקשה תישלח לבדיקה. ההזמנה תיווצר רק לאחר האישור שלנו.</p>${reviewSubmit()}${quote.merchandise < quote.minimum ? `<p class="b2b-desk-info">מינימום הזמנה ${money(quote.minimum)}</p>` : ""}</div></aside></div><div class="b2b-desk-mobile-review-action"><span><small>סה״כ${quote.hash ? "" : " משוער"}</small><strong>${money(quote.total)}</strong></span>${reviewSubmit()}</div>`;
        bind();
        return;
      }
      const visible = data.products.filter((p) =>
        (p.title + " " + p.variants.map((v) => v.sku).join(" "))
          .toLowerCase()
          .includes(search.toLowerCase()),
      );
      if (!visible.some((p) => p.id === selected))
        selected = visible[0]?.id || "";
      root.innerHTML = `${errorHtml}${mobileControls()}<div class="b2b-desk-layout"><nav id="catalog-${escape(el.id)}" class="b2b-desk-products" aria-label="${t("Products", "מוצרים")}"><header class="b2b-desk-catalog-head"><h2>קטלוג מוצרים</h2><span>${visible.length} פריטים</span></header><input type="search" data-search placeholder="${t("Find a product or SKU", "חיפוש מוצר או מק״ט")}" aria-label="${t("Find a product or SKU", "חיפוש מוצר או מק״ט")}" value="${escape(search)}"><div class="b2b-desk-catalog-list">${productNavigation(visible)}</div></nav><div class="b2b-desk-center">${selected ? matrix(visible.find((p) => p.id === selected)) : `<p class="b2b-desk-loading">${t("No products match.", "לא נמצאו מוצרים.")}</p>`}</div><div data-summary>${summary()}</div></div>${summary(true)}`;
      bind();
      fitHeight();
    }
    function bind() {
      root.querySelectorAll("[data-mobile-view]").forEach((button) => {
        button.onclick = () => {
          if (busy) return;
          mobileView = button.dataset.mobileView;
          mobileCatalogOpen = false;
          render();
          root
            .querySelector(`[data-mobile-view="${mobileView}"]`)
            ?.focus({ preventScroll: true });
        };
      });
      const picker = root.querySelector("[data-mobile-catalog]");
      if (picker)
        picker.onclick = () => {
          if (busy) return;
          mobileCatalogOpen = !mobileCatalogOpen;
          root.dataset.catalogOpen = String(mobileCatalogOpen);
          picker.setAttribute("aria-expanded", String(mobileCatalogOpen));
        };
      if (busy)
        root.querySelectorAll("input, textarea").forEach((control) => {
          control.disabled = true;
        });
      root.querySelectorAll("[data-group]").forEach((group) =>
        group.addEventListener("toggle", () => {
          if (group.open) collapsedGroups.delete(group.dataset.group);
          else collapsedGroups.add(group.dataset.group);
        }),
      );
      root.querySelector(".b2b-desk-center")?.addEventListener(
        "wheel",
        (e) => {
          if (
            el.dataset.fitViewport !== "true" ||
            window.innerWidth <= 1100 ||
            e.ctrlKey ||
            e.shiftKey ||
            e.deltaX
          )
            return;
          const rect = root
            .querySelector(".b2b-desk-layout")
            .getBoundingClientRect();
          if (rect.top < 0 || rect.bottom > window.innerHeight) return;
          const table = root.querySelector(
            ".b2b-desk-product .b2b-desk-table-scroll",
          );
          if (table && table.scrollHeight <= table.clientHeight)
            e.preventDefault();
        },
        { passive: false },
      );
      root.querySelectorAll("[data-product]").forEach((b) =>
        b.addEventListener("click", () => {
          selected = b.dataset.product;
          if (isMobile()) mobileCatalogOpen = false;
          render();
          if (isMobile())
            root
              .querySelector("[data-mobile-catalog]")
              ?.focus({ preventScroll: true });
        }),
      );
      root.querySelector("[data-search]")?.addEventListener("input", (e) => {
        search = e.target.value;
        const pos = e.target.selectionStart;
        render();
        const input = root.querySelector("[data-search]");
        input.focus();
        try {
          input.setSelectionRange(pos, pos);
        } catch {}
      });
      root.querySelectorAll("[data-variant]").forEach((input) =>
        input.addEventListener("focus", () => {
          const v = data.products
            .flatMap((p) => p.variants)
            .find((v) => v.id === input.dataset.variant);
          const message = root.querySelector("[data-grid-message]");
          if (message)
            message.textContent =
              input.getAttribute("aria-invalid") === "true"
                ? quantityHelp(v, Number(input.value))
                : "";
        }),
      );
      root
        .querySelectorAll("[data-variant]")
        .forEach((input) =>
          input.addEventListener("input", () =>
            updateQuantity(input, input.dataset.variant),
          ),
        );
      root.oninput = (event) => {
        if (event.target.matches("[data-summary-variant]"))
          updateQuantity(
            event.target,
            event.target.dataset.summaryVariant,
            true,
          );
      };
      root.onchange = (event) => {
        if (
          event.target.matches("[data-summary-variant]") &&
          !Number(event.target.value)
        )
          updateQuantity(event.target, event.target.dataset.summaryVariant);
      };
      root.querySelectorAll("[data-field]").forEach((input) =>
        input.addEventListener("input", () => {
          if (input.dataset.field === "notes") notes = input.value;
          save();
        }),
      );
      bindActions();
    }
    function updateQuantity(input, variantId, keepSummary = false) {
      if (busy) return;
      quantities[variantId] = Number(input.value);
      const v = data.products
        .flatMap((p) => p.variants)
        .find((v) => v.id === variantId);
      const q = Number(input.value);
      if (q === 0) input.value = "";
      input.setAttribute(
        "aria-invalid",
        String(
          q !== 0 &&
            (!Number.isInteger(q) ||
              q > v.max ||
              q < v.minimum ||
              q % v.increment !== 0),
        ),
      );
      const help = input.parentElement.querySelector(".b2b-desk-quantity-help");
      if (help) {
        help.hidden = input.getAttribute("aria-invalid") !== "true";
        help.textContent = quantityHelp(v, q);
      }
      const gridMessage = root.querySelector("[data-grid-message]");
      if (gridMessage)
        gridMessage.textContent =
          input.getAttribute("aria-invalid") === "true"
            ? quantityHelp(v, q)
            : "";
      input.title = quantityHelp(v, q);
      root.querySelectorAll("[data-product]").forEach((b) => {
        const p = data.products.find((p) => p.id === b.dataset.product);
        const count = p.variants.reduce(
          (n, v) => n + (Number(quantities[v.id]) || 0),
          0,
        );
        b.querySelector("strong")?.remove();
        if (count)
          b.insertAdjacentHTML("beforeend", `<strong>${count}</strong>`);
      });
      const currentProduct = data.products.find((p) => p.id === selected);
      const priceLabel = root.querySelector(".b2b-desk-product-price strong");
      if (currentProduct)
        root.querySelector("[data-product-subtotal]").innerHTML =
          productTotal(currentProduct);
      if (priceLabel && currentProduct?.variants.length) {
        const values = currentProduct.variants.map((v) =>
          price(v, Number(quantities[v.id]) || 1),
        );
        const low = Math.min(...values),
          high = Math.max(...values);
        priceLabel.textContent =
          money(low) + (high !== low ? "–" + money(high) : "");
      }
      quote = null;
      save();
      const pane = root.querySelector("[data-summary]");
      const scroll =
        pane.querySelector(".b2b-desk-summary-lines")?.scrollTop || 0;
      if (keepSummary) {
        const template = document.createElement("template");
        template.innerHTML = summary();
        pane
          .querySelector(".b2b-desk-summary-head")
          .replaceWith(
            template.content.querySelector(".b2b-desk-summary-head"),
          );
        pane
          .querySelector(".b2b-desk-summary-footer")
          .replaceWith(
            template.content.querySelector(".b2b-desk-summary-footer"),
          );
        const row = input.closest("[data-summary-row]");
        if (row) {
          row.querySelector("[data-line-amount]").textContent = money(
            price(v, q) * Math.max(0, q),
          );
          const rowHelp = row.querySelector(".b2b-desk-line-error");
          rowHelp.hidden = !quantityInvalid(v, q);
          rowHelp.textContent = quantityHelp(v, q);
        }
      } else {
        pane.innerHTML = summary();
        pane.querySelector(".b2b-desk-summary-lines").scrollTop = scroll;
      }
      const gridInput = [...root.querySelectorAll("[data-variant]")].find(
        (i) => i.dataset.variant === variantId,
      );
      if (gridInput && gridInput !== input) {
        gridInput.value = q || "";
        gridInput.setAttribute("aria-invalid", String(quantityInvalid(v, q)));
        const gridHelp = gridInput.parentElement.querySelector(
          ".b2b-desk-quantity-help",
        );
        if (gridHelp) {
          gridHelp.hidden = !quantityInvalid(v, q);
          gridHelp.textContent = quantityHelp(v, q);
        }
        gridInput.title = quantityHelp(v, q);
      }
      const mobileCount = root.querySelector("[data-mobile-count]");
      if (mobileCount) mobileCount.textContent = String(estimate().count);
      root.querySelector(".b2b-desk-mobile-summary").outerHTML = summary(true);
      bindActions();
    }
    function bindActions() {
      root.querySelectorAll("[data-action]").forEach((button) => {
        button.onclick = async () => {
          if (busy) return;
          const action = button.dataset.action;
          if (action === "review") {
            const e = estimate();
            if (e.merchandise < data.minimum || e.invalid || !e.chosen.length)
              return;
          }
          if (
            action === "submit" &&
            (!quote?.hash || quote.merchandise < quote.minimum)
          )
            return;
          error = "";
          if (action === "back") {
            stage = "build";
            render();
            return;
          }
          if (action === "new") {
            stage = "build";
            render();
            return;
          }
          try {
            busy = true;
            if (action === "review") {
              const estimateNow = estimate();
              quote = {
                ...estimateNow,
                minimum: data.minimum,
                hash: null,
                lines: selectedLines().map(({ p, v, q }) => ({
                  title: p.title,
                  variantTitle: v.title,
                  sku: v.sku,
                  quantity: q,
                })),
              };
              stage = "review";
            }
            render();
            if (action === "review") {
              const result = await api({ intent: "quote", lines: lines() });
              quote = result.quote;
              stage = "review";
            }
            if (action === "submit") {
              await api({
                intent: "submit",
                lines: lines(),
                quoteHash: quote.hash,
                idempotencyKey: idempotency,
                notes,
              });
              stage = "done";
              quantities = {};
              notes = "";
              idempotency = crypto.randomUUID().replaceAll("-", "");
              save();
            }
          } catch (e) {
            if (action === "review") {
              stage = "build";
              quote = null;
            }
            error = message(e);
          } finally {
            busy = false;
            render();
            root
              .querySelector("[role=alert]")
              ?.scrollIntoView({ block: "nearest" });
          }
        };
      });
    }
    // Custom theme buttons need only a normal link; no button markup contract.
    const isPrimaryDesk = () =>
      document.querySelector(
        '[data-b2b-desk][data-display-mode="fullscreen"][data-customer]:not([data-customer=""])',
      ) === el;
    const openFromHash = () => {
      if (
        location.hash === "#b2b-order" &&
        isPrimaryDesk() &&
        !window.Shopify?.designMode
      )
        openWorkspace(null);
    };
    const customLauncher = (event) => {
      if (
        !dialog ||
        !isPrimaryDesk() ||
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      )
        return;
      const link = event.target.closest?.("a[href]");
      if (
        !link ||
        link.hasAttribute("download") ||
        (link.target && link.target !== "_self")
      )
        return;
      const url = new URL(link.href, location.href);
      if (
        url.origin !== location.origin ||
        url.pathname !== location.pathname ||
        url.search !== location.search ||
        url.hash !== "#b2b-order"
      )
        return;
      event.preventDefault();
      openWorkspace(link);
    };
    document.addEventListener("click", customLauncher);
    window.addEventListener("hashchange", openFromHash);
    openFromHash();
    function fitMobileViewport() {
      if (dialog && isMobile()) {
        const height = Math.round(
          window.visualViewport?.height || window.innerHeight,
        );
        dialog.style.setProperty("--desk-mobile-height", `${height}px`);
        dialog.dataset.shortViewport = String(height < 360);
      }
    }
    const resize = () => {
      if (el.isConnected) {
        fitHeight();
        fitMobileViewport();
      } else {
        window.removeEventListener("resize", resize);
        window.visualViewport?.removeEventListener("resize", resize);
      }
    };
    window.addEventListener("resize", resize);
    window.visualViewport?.addEventListener("resize", resize);
    fitMobileViewport();
    const unload = (e) => {
      if (e.target.contains(el)) {
        if (dialog?.open) dialog.close();
        window.removeEventListener("resize", resize);
        window.visualViewport?.removeEventListener("resize", resize);
        document.removeEventListener("click", customLauncher);
        window.removeEventListener("hashchange", openFromHash);
        document.removeEventListener("shopify:section:unload", unload);
      }
    };
    document.addEventListener("shopify:section:unload", unload);
    const loadCatalog = () =>
      fetch(endpoint)
        .then(async (response) => {
          const result = await response.json();
          if (!response.ok || result.error)
            throw new Error(
              result.error || "לא ניתן לטעון את המוצרים. נסו לרענן.",
            );
          return result;
        })
        .then((result) => {
          data = result;
          const allowed = new Set(
            data.products.flatMap((p) => p.variants.map((v) => v.id)),
          );
          quantities = Object.fromEntries(
            Object.entries(quantities).filter(([id]) => allowed.has(id)),
          );
          selected = data.products[0]?.id || "";
          render();
        })
        .catch((e) => {
          root.innerHTML = `<p class="b2b-desk-error" role="alert">${escape(message(e))}</p><button class="b2b-desk-button" data-retry>${t("Try again", "ניסיון נוסף")}</button>`;
          root.querySelector("[data-retry]").onclick = () => {
            root.innerHTML = `<p>${t("Loading…", "טוענים…")}</p>`;
            loadCatalog();
          };
        });
    loadCatalog();
  }
  document.querySelectorAll("[data-b2b-desk]").forEach(init);
  document.addEventListener("shopify:section:load", (e) =>
    e.target.querySelectorAll("[data-b2b-desk]").forEach(init),
  );
})();
